import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getContactMessages,
  getWaitlistSubmissions,
  type ContactMessage,
  type WaitlistSubmission,
} from "@/lib/messages.functions";

export const Route = createFileRoute("/_authenticated/messages")({
  head: () => ({
    meta: [
      { title: "Contact messages — adaptED admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MessagesPage,
});

type Tab = "contact" | "waitlist";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString("en-ZA", {
    timeZone: "Africa/Johannesburg",
    dateStyle: "medium",
    timeStyle: "short",
  });

const KIND_LABELS: Record<string, string> = {
  school_application: "School application",
  school_nomination: "School nomination",
  individual_early_access: "Individual early access",
};

function MessagesPage() {
  const { user } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("contact");
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<WaitlistSubmission | null>(null);

  const messagesQuery = useQuery({
    queryKey: ["contact-messages"],
    queryFn: () => getContactMessages(),
  });

  const waitlistQuery = useQuery({
    queryKey: ["waitlist-submissions"],
    queryFn: () => getWaitlistSubmissions(),
    enabled: tab === "waitlist",
  });

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
  }

  const active = tab === "contact" ? messagesQuery : waitlistQuery;

  return (
    <main className="min-h-dvh bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">
              Form submissions
            </h1>
            <p className="text-xs text-muted-foreground">
              Signed in as {user.email}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="rounded-full border border-input px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex gap-2">
          {(["contact", "waitlist"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === t
                  ? "bg-primary text-primary-foreground"
                  : "border border-input text-foreground hover:bg-accent"
              }`}
            >
              {t === "contact" ? "Contact messages" : "Pilot & nominations"}
            </button>
          ))}
        </div>

        {active.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : active.error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {(active.error as Error).message}
          </div>
        ) : tab === "contact" ? (
          !messagesQuery.data || messagesQuery.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No messages yet. New contact form submissions will appear here.
            </p>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              <ul className="space-y-2">
                {messagesQuery.data.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(m)}
                      className={`w-full rounded-lg border p-4 text-left transition-colors ${
                        selected?.id === m.id
                          ? "border-primary bg-primary/5"
                          : "border-border bg-card hover:border-primary/40"
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold text-foreground">{m.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {fmtDate(m.created_at)}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">
                        {m.subject || m.message}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground/80">
                        {m.email}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="rounded-lg border border-border bg-card p-6">
                {selected ? (
                  <div>
                    <h2 className="font-display text-lg font-bold text-foreground">
                      {selected.subject || "(no subject)"}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      From{" "}
                      <a
                        href={`mailto:${selected.email}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {selected.name} {"<"}
                        {selected.email}
                        {">"}
                      </a>
                    </p>
                    {selected.phone && (
                      <p className="text-sm text-muted-foreground">
                        Phone: {selected.phone}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      Received {fmtDate(selected.created_at)}
                    </p>
                    <div className="mt-5 whitespace-pre-wrap rounded-lg bg-background p-4 text-sm leading-relaxed text-foreground">
                      {selected.message}
                    </div>
                    <a
                      href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(
                        selected.subject || "Your enquiry",
                      )}`}
                      className="mt-5 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                    >
                      Reply by email
                    </a>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Select a message to read it.
                  </p>
                )}
              </div>
            </div>
          )
        ) : !waitlistQuery.data || waitlistQuery.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No pilot applications or nominations yet.
          </p>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <ul className="space-y-2">
              {waitlistQuery.data.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedEntry(s)}
                    className={`w-full rounded-lg border p-4 text-left transition-colors ${
                      selectedEntry?.id === s.id
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-foreground">{s.full_name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {fmtDate(s.created_at)}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {KIND_LABELS[s.submission_kind] || s.submission_kind}
                      {s.school_name ? ` · ${s.school_name}` : ""}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground/80">
                      {s.email}
                    </p>
                  </button>
                </li>
              ))}
            </ul>

            <div className="rounded-lg border border-border bg-card p-6">
              {selectedEntry ? (
                <div>
                  <h2 className="font-display text-lg font-bold text-foreground">
                    {KIND_LABELS[selectedEntry.submission_kind] ||
                      selectedEntry.submission_kind}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    From{" "}
                    <a
                      href={`mailto:${selectedEntry.email}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {selectedEntry.full_name} {"<"}
                      {selectedEntry.email}
                      {">"}
                    </a>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Received {fmtDate(selectedEntry.created_at)}
                  </p>

                  <dl className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                    {(
                      [
                        ["Role / portal", selectedEntry.portal_type],
                        ["Phone", selectedEntry.phone],
                        ["School", selectedEntry.school_name],
                        ["Position", selectedEntry.contact_position],
                        ["School location", selectedEntry.school_location],
                        ["Province", selectedEntry.province],
                        ["Grade", selectedEntry.grade],
                        ["Matric learners", selectedEntry.matric_learner_count],
                        ["Biggest challenge", selectedEntry.biggest_challenge],
                        ["Challenge detail", selectedEntry.biggest_challenge_other],
                        ["Consent given", selectedEntry.consent_given ? "Yes" : "No"],
                      ] as const
                    )
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {label}
                          </dt>
                          <dd className="text-sm text-foreground">{value}</dd>
                        </div>
                      ))}
                  </dl>

                  {selectedEntry.message && (
                    <div className="mt-5 whitespace-pre-wrap rounded-lg bg-background p-4 text-sm leading-relaxed text-foreground">
                      {selectedEntry.message}
                    </div>
                  )}

                  <a
                    href={`mailto:${selectedEntry.email}?subject=${encodeURIComponent(
                      "adaptED pilot programme",
                    )}`}
                    className="mt-5 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                  >
                    Reply by email
                  </a>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Select a submission to see the full details.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
